import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableSkeleton,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { MnoIpRangeList } from '@/components/features/mno-ip-ranges/mno-ip-range-list';
import { IpRangeExportMenu } from '@/components/features/mno-ip-ranges/ip-range-export-menu';
import {
  useDeleteMnoIpRange,
  useMnoIpRanges,
  useMnoOperators,
} from '@/hooks/use-mno-ip-ranges';
import type { MnoIpRangeFilters } from '@/lib/api/mno-ip-ranges';
import type { MnoIpRangeStatus } from '@/types/mno-ip-range';
import { getCountryOptions } from '@/lib/data/iso-3166';
import { useAuth } from '@/hooks/use-auth';

const ALL = '__all__';

/**
 * Platform-Verwaltung der MNO-IP-Ranges (Issue #318, #326 echte Daten).
 * CRUD nur fuer platform_admin; platform_it sieht die Liste read-only.
 * Filter (Land/Operator/Status) werden serverseitig an den BFF durchgereicht.
 */
export function MnoIpRangesPage() {
  const { t, i18n } = useTranslation('mno-ip-ranges');
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCrud = user?.role === 'platform_admin';

  const [filters, setFilters] = useState<MnoIpRangeFilters>({});

  const { data: entries, isLoading, error, refetch } = useMnoIpRanges(filters);
  const { data: operators } = useMnoOperators();
  const deleteMutation = useDeleteMnoIpRange();

  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language],
  );

  const setFilter = (key: keyof MnoIpRangeFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === ALL) delete next[key];
      else next[key] = value as MnoIpRangeStatus & string;
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        title={t('platform.title')}
        description={t('platform.description')}
        actions={
          <div className="flex gap-2">
            <IpRangeExportMenu entries={entries ?? []} operators={operators ?? []} />
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            {canCrud && (
              <Button size="sm" onClick={() => navigate('/mno-ip-ranges/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('platform.new')}
              </Button>
            )}
          </div>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-4 py-4">
          <div className="grid gap-1.5">
            <Label htmlFor="filter-country">{t('filters.country')}</Label>
            <Select value={filters.country ?? ALL} onValueChange={(v) => setFilter('country', v)}>
              <SelectTrigger id="filter-country" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.allCountries')}</SelectItem>
                {countryOptions.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="filter-operator">{t('filters.operator')}</Label>
            <Select value={filters.operatorId ?? ALL} onValueChange={(v) => setFilter('operatorId', v)}>
              <SelectTrigger id="filter-operator" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.allOperators')}</SelectItem>
                {(operators ?? []).map((op) => (
                  <SelectItem key={op.operatorId} value={op.operatorId}>
                    {op.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="filter-status">{t('filters.status')}</Label>
            <Select value={filters.status ?? ALL} onValueChange={(v) => setFilter('status', v)}>
              <SelectTrigger id="filter-status" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.allStatuses')}</SelectItem>
                <SelectItem value="active">{t('status.active')}</SelectItem>
                <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={4} cols={3} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && entries && (
        <MnoIpRangeList
          entries={entries}
          operators={operators ?? []}
          readOnly={!canCrud}
          onEdit={(opId, cc) =>
            navigate(`/mno-ip-ranges/${encodeURIComponent(opId)}/${encodeURIComponent(cc)}`)
          }
          onDelete={(opId, cc) => deleteMutation.mutate({ operatorId: opId, countryCode: cc })}
        />
      )}
    </div>
  );
}
